/**
 * Modalities Management Page
 *
 * CRUD for imaging modalities (ADMIN/SUPER_ADMIN only)
 * Allows creating, editing, reordering, and toggling modalities
 */

import { useState, useMemo, useEffect } from 'react'
import {
  Layers,
  Plus,
  Search,
  Power,
  PowerOff,
  Trash2,
  Edit,
  GripVertical,
  Loader2,
  AlertCircle,
  Check,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import {
  useModalities,
  useCreateModality,
  useUpdateModality,
  useDeleteModality,
  useToggleModality,
} from '@/application/modalities'
import { useAuth } from '@/application/auth'
import { useInstitutions } from '@/application/institutions'
import { USER_ROLES } from '@/config/constants'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Modality, CreateModalityData, UpdateModalityData } from '@/domain/modality/types'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'active', label: 'Activas' },
  { value: 'inactive', label: 'Inactivas' },
]

// =============================================================================
// Modality Form Modal
// =============================================================================

interface ModalityFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  modality?: Modality | null
  onSuccess?: () => void
}

function ModalityFormModal({
  open,
  onOpenChange,
  modality,
  onSuccess,
}: ModalityFormModalProps) {
  const isEditing = !!modality

  const [code, setCode] = useState(modality?.code || '')
  const [name, setName] = useState(modality?.name || '')
  const [description, setDescription] = useState(modality?.description || '')
  const [displayOrder, setDisplayOrder] = useState(modality?.displayOrder?.toString() || '')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateModality()
  const updateMutation = useUpdateModality()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!code.trim()) {
      setError('El código es requerido')
      return
    }

    if (!name.trim()) {
      setError('El nombre es requerido')
      return
    }

    try {
      if (isEditing && modality) {
        const updateData: UpdateModalityData = {
          name: name.trim(),
          description: description.trim() || undefined,
          displayOrder: displayOrder ? parseInt(displayOrder, 10) : undefined,
        }
        await updateMutation.mutateAsync({ id: modality.id, data: updateData })
        toast.success('Tipo de estudio actualizado correctamente')
      } else {
        const createData: CreateModalityData = {
          code: code.trim().toUpperCase(),
          name: name.trim(),
          description: description.trim() || undefined,
          displayOrder: displayOrder ? parseInt(displayOrder, 10) : undefined,
        }
        await createMutation.mutateAsync(createData)
        toast.success('Tipo de estudio creado correctamente')
      }

      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al guardar el tipo de estudio')
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setCode('')
    setName('')
    setDescription('')
    setDisplayOrder('')
    setError(null)
    onOpenChange(false)
  }

  // Reset form when modality changes or modal opens
  useEffect(() => {
    if (open && modality) {
      setCode(modality.code)
      setName(modality.name)
      setDescription(modality.description || '')
      setDisplayOrder(modality.displayOrder?.toString() || '')
    } else if (open && !modality) {
      // Reset for create mode
      setCode('')
      setName('')
      setDescription('')
      setDisplayOrder('')
    }
    setError(null)
  }, [open, modality])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Tipo de Estudio' : 'Nuevo Tipo de Estudio'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique los datos del tipo de estudio'
              : 'Complete los datos para crear un nuevo tipo de estudio'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ej: DX, CT, MR"
              maxLength={10}
              disabled={isEditing}
            />
            {isEditing && (
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                El código no se puede cambiar
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Radiografía Digital"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Descripción
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción opcional..."
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm resize-none"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Orden de visualización
            </label>
            <Input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              placeholder="1, 2, 3..."
              min={1}
            />
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Determina el orden en listas y selectores
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isEditing ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  {isEditing ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Crear
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  modality: Modality | null
  onConfirm: () => void
  isPending: boolean
}

function DeleteModal({
  open,
  onOpenChange,
  modality,
  onConfirm,
  isPending,
}: DeleteModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Eliminar Tipo de Estudio</DialogTitle>
          <DialogDescription>
            ¿Está seguro que desea eliminar el tipo de estudio <strong>{modality?.name}</strong> ({modality?.code})?
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 mt-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">
            Los estudios existentes con este tipo de estudio no serán afectados.
          </span>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function ModalitiesPage() {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN

  // State
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingModality, setEditingModality] = useState<Modality | null>(null)
  const [deletingModality, setDeletingModality] = useState<Modality | null>(null)

  // Queries
  const { data: modalitiesData, isLoading } = useModalities({
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    institutionId: institutionFilter || undefined,
  })

  const { data: institutionsData } = useInstitutions({ limit: 100 })

  const toggleMutation = useToggleModality()
  const deleteMutation = useDeleteModality()

  // Filter modalities by search
  const filteredModalities = useMemo(() => {
    if (!modalitiesData?.modalities) return []

    let result = [...modalitiesData.modalities]

    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(
        (m) =>
          m.code.toLowerCase().includes(searchLower) ||
          m.name.toLowerCase().includes(searchLower)
      )
    }

    // Sort by displayOrder
    result.sort((a, b) => a.displayOrder - b.displayOrder)

    return result
  }, [modalitiesData?.modalities, search])

  // Institution options for SUPER_ADMIN
  const institutionOptions = useMemo(() => {
    const options = [{ value: '', label: 'Todas las instituciones' }]
    if (institutionsData?.institutions) {
      options.push(
        ...institutionsData.institutions.map((inst) => ({
          value: inst.id,
          label: inst.name,
        }))
      )
    }
    return options
  }, [institutionsData?.institutions])

  // Handlers
  const handleToggle = (modality: Modality) => {
    toggleMutation.mutate(modality.id, {
      onSuccess: () => {
        toast.success(modality.isActive ? 'Tipo de estudio desactivado' : 'Tipo de estudio activado')
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al cambiar estado del tipo de estudio'))
      },
    })
  }

  const handleDelete = () => {
    if (deletingModality) {
      deleteMutation.mutate(deletingModality.id, {
        onSuccess: () => {
          toast.success('Tipo de estudio eliminado correctamente')
          setDeletingModality(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar el tipo de estudio'))
        },
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tipos de Estudio</h1>
          <p className="text-[var(--foreground-muted)]">
            Catálogo de tipos de estudio por institución
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Tipo de Estudio
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                placeholder="Buscar por código o nombre..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>

            <div className="w-[150px]">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>

            {isSuperAdmin && (
              <div className="w-[200px]">
                <Select
                  options={institutionOptions}
                  value={institutionFilter}
                  onChange={(e) => setInstitutionFilter(e.target.value)}
                />
              </div>
            )}

            <div className="text-sm text-[var(--foreground-muted)] ml-auto">
              {filteredModalities.length} tipos de estudio
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[150px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModalities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Layers className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                      <p className="mt-2 text-[var(--foreground-muted)]">
                        No hay tipos de estudio
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredModalities.map((modality, index) => (
                    <TableRow key={modality.id}>
                      <TableCell className="text-[var(--foreground-muted)]">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 opacity-30" />
                          {index + 1}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {modality.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {modality.name}
                      </TableCell>
                      <TableCell className="text-[var(--foreground-muted)] max-w-[200px] truncate">
                        {modality.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={modality.isActive ? 'success' : 'secondary'}>
                          {modality.isActive ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingModality(modality)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(modality)}
                            title={modality.isActive ? 'Desactivar' : 'Activar'}
                            disabled={toggleMutation.isPending}
                          >
                            {modality.isActive ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingModality(modality)}
                            title="Eliminar"
                            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <ModalityFormModal
        open={showCreateModal || !!editingModality}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false)
            setEditingModality(null)
          }
        }}
        modality={editingModality}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={!!deletingModality}
        onOpenChange={(open) => !open && setDeletingModality(null)}
        modality={deletingModality}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
